import React, { useState } from 'react';
import {
  SquareLibrary,
  KeyRound,
  Sparkles,
  Loader2,
  AlertCircle,
  Copy,
  Check,
  Eye,
  EyeOff,
} from 'lucide-react';

// The user supplies their own Anthropic API key — it is stored only in this
// browser (localStorage) and sent directly to Anthropic, never to us
const KEY_STORAGE = 'osint_anthropic_key';

interface Question {
  id: string;
  label: string;
  placeholder: string;
  big?: boolean;
}

// Standardised prompts mirroring the OSINT Walkthrough phases.
const questions: Question[] = [
  { id: 'objective', label: 'Investigation objective', placeholder: 'What are you trying to learn? One sentence.' },
  { id: 'subject', label: 'Subject & seed identifiers', placeholder: 'Names, usernames, emails, company numbers…' },
  { id: 'corporate', label: 'Corporate & records', placeholder: 'Companies House, directors, filings, addresses…', big: true },
  { id: 'social', label: 'Social media', placeholder: 'Profiles, handles, connections, notable posts…', big: true },
  { id: 'images', label: 'Images & verification', placeholder: 'Reverse-image hits, EXIF, matched faces/places…' },
  { id: 'search', label: 'Search & dorking', placeholder: 'Useful dorks, exposed files, stray mentions…' },
  { id: 'infra', label: 'Domains & infrastructure', placeholder: 'WHOIS, DNS, breaches, exposed hosts…' },
  { id: 'news', label: 'News & media', placeholder: 'Articles, archives, press releases, dates…' },
  { id: 'location', label: 'Location', placeholder: 'Places, chronolocation clues, movement timeline…' },
];

const models = [
  { id: 'claude-opus-4-8', label: 'Opus 4.8 — most capable' },
  { id: 'claude-sonnet-4-6', label: 'Sonnet 4.6 — balanced' },
  { id: 'claude-haiku-4-5', label: 'Haiku 4.5 — fastest / cheapest' },
];

const Aggregation: React.FC = () => {
  const [apiKey, setApiKey] = useState<string>(() => localStorage.getItem(KEY_STORAGE) || '');
  const [showKey, setShowKey] = useState(false);
  const [model, setModel] = useState(models[0].id);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [freeform, setFreeform] = useState('');
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const saveKey = (value: string) => {
    setApiKey(value);
    if (value) localStorage.setItem(KEY_STORAGE, value);
    else localStorage.removeItem(KEY_STORAGE);
  };

  const setAnswer = (id: string, value: string) =>
    setAnswers((prev) => ({ ...prev, [id]: value }));

  const hasInput =
    Object.values(answers).some((v) => v.trim()) || freeform.trim().length > 0;

  const buildFindings = () => {
    const sections = questions
      .filter((q) => (answers[q.id] || '').trim())
      .map((q) => `## ${q.label}\n${answers[q.id].trim()}`);
    if (freeform.trim()) sections.push(`## Additional raw notes\n${freeform.trim()}`);
    return sections.join('\n\n');
  };

  const summarize = async () => {
    setError('');
    setSummary('');
    if (!apiKey.trim()) {
      setError('Enter your Anthropic API key first.');
      return;
    }
    if (!hasInput) {
      setError('Add at least one finding before summarising.');
      return;
    }

    setLoading(true);
    try {
      // Call the Anthropic API directly from the browser. The user's key is sent
      // straight to Anthropic; the dangerous-direct-browser-access header opts in
      // to CORS from the browser (safe here since it's the user's own key).
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'x-api-key': apiKey.trim(),
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model,
          max_tokens: 16000,
          thinking: { type: 'adaptive' },
          system:
            'You are an expert OSINT analyst. You aggregate raw investigation findings into a clear, ' +
            'structured intelligence summary. Only use what the analyst provides — never invent facts. ' +
            'Flag gaps, contradictions, and unverified claims explicitly, and note confidence levels. ' +
            'Structure the output with these sections: Executive Summary, Key Entities, Timeline (if any), ' +
            'Corroboration & Confidence, Gaps & Unknowns, Recommended Next Steps.',
          messages: [
            {
              role: 'user',
              content: `Aggregate and summarise the following OSINT findings.\n\n${buildFindings()}`,
            },
          ],
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 401) setError('Invalid API key — check it and try again.');
        else if (res.status === 429) setError('Rate limited by Anthropic. Wait a moment and retry.');
        else setError(`API error ${res.status}: ${data?.error?.message || res.statusText}`);
        return;
      }

      const text = (data.content || [])
        .filter((b: { type: string }) => b.type === 'text')
        .map((b: { text: string }) => b.text)
        .join('\n');
      setSummary(text || 'No summary returned.');
    } catch (e) {
      setError(
        e instanceof Error
          ? `Request failed: ${e.message}. If this is a network/CORS error, check your key and connection.`
          : 'Something went wrong.',
      );
    } finally {
      setLoading(false);
    }
  };

  const copySummary = async () => {
    await navigator.clipboard.writeText(summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 py-8 px-6">
      <div className="max-w-none">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center text-white">
            <SquareLibrary className="w-7 h-7" />
          </div>
          <div>
            <p className="text-xs font-bold tracking-widest text-orange-500 uppercase mb-1">
              AI Summary
            </p>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-100">Data Aggregation</h1>
          </div>
        </div>
        <p className="text-gray-500 dark:text-slate-400 max-w-2xl mb-8">
          Drop your findings into the standardised fields, then let AI aggregate them into a
          structured intelligence summary. Runs on your own Anthropic API key.
        </p>

        <div className="h-1 w-full bg-gradient-to-r from-orange-500 to-amber-600 mb-8" />

        {/* API key + model */}
        <div className="bg-gray-50 dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-5 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound className="w-5 h-5 text-orange-500" />
            <h3 className="font-bold text-slate-900 dark:text-slate-100">Your Anthropic API key</h3>
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => saveKey(e.target.value)}
                placeholder="sk-ant-..."
                className="w-full rounded-lg border border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500 px-4 py-2.5 pr-10 font-mono text-sm focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
              />
              <button
                type="button"
                onClick={() => setShowKey((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-orange-500"
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="rounded-lg border border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500 px-4 py-2.5 text-sm bg-white dark:bg-slate-800 focus:border-orange-400 focus:outline-none"
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-gray-400 dark:text-slate-500 mt-2">
            Stored only in this browser and sent directly to Anthropic — it never touches our servers.
            Get a key at console.anthropic.com. Requests are billed to your account.
          </p>
        </div>

        {/* Questions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
          {questions.map((q) => (
            <div key={q.id} className={q.big ? 'md:col-span-2' : ''}>
              <label className="block text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">{q.label}</label>
              <textarea
                value={answers[q.id] || ''}
                onChange={(e) => setAnswer(q.id, e.target.value)}
                placeholder={q.placeholder}
                rows={q.big ? 3 : 2}
                className="w-full rounded-lg border border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500 px-3 py-2 text-sm resize-y focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
              />
            </div>
          ))}
        </div>

        {/* Freeform */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">
            Paste or write anything else
          </label>
          <textarea
            value={freeform}
            onChange={(e) => setFreeform(e.target.value)}
            placeholder="Dump raw notes, copied text, transcripts, links, timelines — anything the AI should factor in."
            rows={8}
            className="w-full rounded-lg border border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500 px-3 py-2 text-sm resize-y focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
          />
        </div>

        {/* Action */}
        <button
          onClick={summarize}
          disabled={loading}
          className="w-full md:w-auto bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 disabled:opacity-60 text-white font-bold py-3 px-8 rounded-lg transition-all flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
          {loading ? 'Aggregating…' : 'Aggregate & summarise'}
        </button>

        {error && (
          <div className="mt-5 border-l-4 border-red-500 bg-red-50 dark:bg-red-500/10 p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Result */}
        {summary && (
          <div className="mt-8 bg-slate-900 dark:bg-slate-950 rounded-xl p-6 text-slate-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-orange-400 uppercase text-sm tracking-wider">
                Intelligence Summary
              </h3>
              <button
                onClick={copySummary}
                className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white"
              >
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{summary}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default Aggregation;
